import React from "react";
import LgvTextField from "../../../components/LgvTextField";
import { QuestionItem } from "../../../types/form";
import { getNumberInputConstraints } from "../../../utils/validationRules";

interface TextFieldRendererProps {
  question: QuestionItem;
  questionType: string;
  inputName: string;
  currentValue: unknown;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  handleMultipleEntryChange: (
    inputName: string,
    idx: number,
    value: unknown,
  ) => void;
  handleAddEntry: (inputName: string) => void;
  handleRemoveEntry: (inputName: string, entryIdx: number) => void;
  readOnly?: boolean;
}

const TextFieldRenderer: React.FC<TextFieldRendererProps> = ({
  question,
  questionType,
  inputName,
  currentValue,
  handleChange,
  handleMultipleEntryChange,
  handleAddEntry,
  handleRemoveEntry,
  readOnly = false,
}) => {
  const isTextarea =
    questionType === "textarea" || questionType === "long_text";
  const isNumber = questionType === "number";
  const numberConstraints = isNumber
    ? getNumberInputConstraints(question)
    : {};

  if (question.allowMultipleAnswers) {
    const entriesArray = Array.isArray(currentValue)
      ? currentValue
      : [currentValue ?? ""];

    return (
      <div className="space-y-3">
        {entriesArray.map((entryValue, idx) => (
          <div key={idx} className="space-y-2">
            <div className="flex items-center gap-2">
              <LgvTextField
                id={idx === 0 ? inputName : `${inputName}-${idx}`}
                name={idx === 0 ? inputName : undefined}
                value={String(entryValue ?? "")}
                type={isNumber ? "number" : "text"}
                min={numberConstraints.min}
                max={numberConstraints.max}
                step={numberConstraints.step}
                inputMode={isNumber ? "numeric" : undefined}
                multiline={isTextarea}
                disabled={readOnly}
                required={question.isRequired}
                placeholder={question.placeholder}
                onChange={(value) => {
                  if (isNumber) {
                    handleMultipleEntryChange(
                      inputName,
                      idx,
                      value === "" ? "" : Number(value),
                    );
                    return;
                  }
                  handleMultipleEntryChange(inputName, idx, value);
                }}
                className="flex-1"
              />
              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => handleRemoveEntry(inputName, idx)}
                  disabled={readOnly}
                  className="px-3 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => handleAddEntry(inputName)}
          disabled={readOnly}
          className="px-4 py-2 bg-[#1B365D] text-white rounded-md hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Add Another
        </button>
      </div>
    );
  }

  return (
    <LgvTextField
      id={inputName}
      name={inputName}
      value={String(currentValue ?? "")}
      type={isNumber ? "number" : "text"}
      min={numberConstraints.min}
      max={numberConstraints.max}
      step={numberConstraints.step}
      inputMode={isNumber ? "numeric" : undefined}
      multiline={isTextarea}
      disabled={readOnly}
      required={question.isRequired}
      placeholder={question.placeholder}
      onChange={(value) => {
        const nextValue = isNumber
          ? value === ""
            ? ""
            : Number(value)
          : value;

        // Through handleChange for numbers too: it is the applicant-edit path,
        // so a count that sizes a list (the number of children) resizes it.
        // Writing straight to the form state skipped that, and lowering the
        // number of children left every entry on the page.
        handleChange({
          target: { name: inputName, value: nextValue },
        } as React.ChangeEvent<HTMLInputElement>);
      }}
    />
  );
};

export default TextFieldRenderer;
